import { Injectable } from '@angular/core';

/**
 * Service for parsing and formatting dates specific to service operations.
 * Handles timezone adjustments and date component extraction.
 *
 * Category: Helper Service
 * Purpose: Date parsing, component extraction, and timezone handling
 */
@Injectable()
export class DateParserService {

  constructor() { }

  /**
   * Parses a timestamp and returns date components adjusted for timezone
   * @param timestamp - Unix timestamp in milliseconds
   * @returns Object with date, time components, and ISO string
   */
  parseDateWithTimezone(timestamp: number): {
    isoString: string;
    fecha: string;
    hora: string;
    minutos: string;
  } {
    const date = new Date(timestamp);
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    const isoString = date.toISOString().slice(0, -1);
    const parts = isoString.split('T');
    const timeParts = parts[1].split(':');

    return {
      isoString: isoString,
      fecha: parts[0],
      hora: timeParts[0],
      minutos: timeParts[1]
    };
  }

  /**
   * Parses service dates (inicio, fin, regreso) and returns all components
   * @param servicio - Service object with date timestamps
   * @returns Object with all parsed date components
   */
  parseServiceDates(servicio: any): {
    inicioDeServicio: any;
    inicioDeServicioFecha: string;
    horaInicioServicio: string;
    minutosInicioServicio: string;
    finDeServicio: any;
    finDeServicioFecha: string;
    horaFinServicio: string;
    minutosFinServicio: string;
    regresoAOrigen: any;
    regresoAOrigenFecha: string;
    horaRegresoAOrigen: string;
    minutosRegresoAOrigen: string;
  } {
    const inicio = this.parseDateWithTimezone(servicio.inicioDeServicio);
    const fin = this.parseDateWithTimezone(servicio.finDeServicio);
    const regreso = this.parseDateWithTimezone(servicio.regresoAOrigen);

    return {
      inicioDeServicio: inicio.isoString,
      inicioDeServicioFecha: inicio.fecha,
      horaInicioServicio: inicio.hora,
      minutosInicioServicio: inicio.minutos,
      finDeServicio: fin.isoString,
      finDeServicioFecha: fin.fecha,
      horaFinServicio: fin.hora,
      minutosFinServicio: fin.minutos,
      regresoAOrigen: regreso.isoString,
      regresoAOrigenFecha: regreso.fecha,
      horaRegresoAOrigen: regreso.hora,
      minutosRegresoAOrigen: regreso.minutos
    };
  }

  /**
   * Combines date and time components into a timestamp
   * @param fecha - Date in YYYY-MM-DD format
   * @param hora - Hour (0-23)
   * @param minutos - Minutes (0-59)
   * @returns Unix timestamp in milliseconds
   */
  combineDateTime(fecha: string, hora: string, minutos: string): number {
    const dateTimeString = `${fecha} ${hora}:${minutos}`;
    return new Date(dateTimeString).getTime();
  }

  /**
   * Calculates hours between two timestamps
   * @param inicioTimestamp - Start timestamp
   * @param finTimestamp - End timestamp
   * @returns Number of hours between the two timestamps
   */
  calculateHoursBetween(inicioTimestamp: number, finTimestamp: number): number {
    return (finTimestamp - inicioTimestamp) / 1000 / 60 / 60;
  }

  /**
   * Formats a date for change tracking notes
   * @returns Formatted date string
   */
  formatChangeDate(): string {
    const now = new Date();
    return `${now.toDateString()} ${now.toLocaleTimeString()}`;
  }

  /**
   * Parses a date range from string format (YYYY-MM-DD) to timestamps
   * @param fechaInicial Initial date string in format YYYY-MM-DD
   * @param fechaFinal Final date string in format YYYY-MM-DD
   * @returns Object with fechaInicial (start of day) and fechaFinal (end of day) as timestamps
   */
  parseDateRange(fechaInicial: string, fechaFinal: string): { fechaInicial: number, fechaFinal: number } {
    const fechaInicialSplit = fechaInicial.split('-');
    const inicial = new Date(
      Number(fechaInicialSplit[0]),
      Number(fechaInicialSplit[1]) - 1,
      Number(fechaInicialSplit[2])
    ).setHours(0, 0, 0).valueOf();

    const fechaFinalSplit = fechaFinal.split('-');
    const final = new Date(
      Number(fechaFinalSplit[0]),
      Number(fechaFinalSplit[1]) - 1,
      Number(fechaFinalSplit[2])
    ).setHours(23, 59, 59, 999).valueOf();

    return {
      fechaInicial: inicial,
      fechaFinal: final
    };
  }

  /**
   * Validates that initial date is not greater than final date
   * @param fechaInicial Initial timestamp
   * @param fechaFinal Final timestamp
   * @returns true if valid, false if initial > final
   */
  validateDateRange(fechaInicial: number, fechaFinal: number): boolean {
    return fechaInicial <= fechaFinal;
  }

  /**
   * Parses date range and validates it
   * @param fechaInicial Initial date string
   * @param fechaFinal Final date string
   * @returns Parsed dates object or null if invalid
   */
  parseDateRangeWithValidation(fechaInicial: string, fechaFinal: string): { fechaInicial: number, fechaFinal: number } | null {
    const dates = this.parseDateRange(fechaInicial, fechaFinal);

    if (!this.validateDateRange(dates.fechaInicial, dates.fechaFinal)) {
      return null;
    }

    return dates;
  }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/date-parser.service.ts